import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Activity, Copy, KeyRound, Loader2, ShieldCheck, ShieldOff, Server } from "lucide-react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import api from "@/lib/api";

/* Tester portal — the tester's own view: status, kit (key / branch code / Tester ID), join steps,
   their telemetry sessions with the bottleneck verdict, and a telemetry pause switch. */

const copyText = (v, label) => { navigator.clipboard?.writeText(v); toast.success(`${label} copied`); };

function Code({ label, value, testId }) {
  return (
    <div className="border border-white/10 bg-black/50 p-4">
      <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500">{label}</p>
      <div className="flex items-center gap-3 mt-2">
        <code data-testid={testId} className="font-mono text-sm sm:text-base text-white tracking-[0.15em] break-all">{value}</code>
        <button data-testid={`${testId}-copy`} onClick={() => copyText(value, label)} className="ml-auto text-zinc-400 hover:text-white transition-colors shrink-0"><Copy className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

const STATUS_COPY = {
  waitlisted: ["On the waitlist", "We bring testers in in waves so the server fills at the rate we want to measure. You'll get one email the moment your spot opens."],
  approved: ["Approved — see you on the server", "Your kit is below. Install now so the test branch is a quick patch."],
  active: ["Active tester", "Telemetry is flowing — thank you. Your sessions and what they tell us are below."],
  revoked: ["Access revoked", "This Tester ID no longer accepts telemetry. Message the organiser if you think this is a mistake."],
  declined: ["Not this round", "Join the Discord for the next round."],
};

export default function TesterPortalPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || localStorage.getItem("nc_tester_token") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/testing/me", { params: { token } }).then((r) => { setData(r.data); localStorage.setItem("nc_tester_token", token); }).catch((e) => setErr(e.response?.data?.detail || "Could not load your portal"));
  useEffect(() => { if (token) load(); else setErr("Open this page from the link in your tester email."); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTelemetry = async () => {
    const on = !data.tester.consent?.telemetry;
    try { const r = await api.post("/testing/consent", { token, telemetry: on }); toast.success(r.data.message); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (err) return <div className="min-h-screen bg-[#050505] text-zinc-100"><SiteNav /><div className="max-w-xl mx-auto px-6 py-32 text-center"><p data-testid="portal-error" className="font-mono text-sm text-zinc-400">{err}</p><Link to="/testing" className="inline-block mt-6 font-mono text-xs text-nc-orange underline">Back to the test program</Link></div></div>;
  if (!data) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-6 h-6 text-nc-orange animate-spin" /></div>;
  const { tester: t, program: p, sessions } = data;
  const [title, sub] = STATUS_COPY[t.status] || STATUS_COPY.waitlisted;
  const telemetryOn = !!t.consent?.telemetry;
  const inProgram = t.status === "approved" || t.status === "active";

  return (
    <div data-testid="tester-portal" className="min-h-screen bg-[#050505] text-zinc-100">
      <Seo title={`Tester portal — ${p.name}`} description="Your New Citizen test kit and telemetry." />
      <SiteNav />
      <div className="max-w-5xl mx-auto px-6 py-16 grid lg:grid-cols-[1.2fr_1fr] gap-10">
        <div>
          <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase">Tester portal // {t.name}</p>
          <h1 data-testid="portal-status-title" className="font-sans font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white mt-3">{title}</h1>
          <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed">{sub}</p>

          {inProgram && (
            <div className="mt-8 space-y-3">
              {t.steam_key && <Code label="Steam key (skip if you own the game)" value={t.steam_key} testId="portal-steam-key" />}
              {data.build_code ? <Code label={`Branch access code — ${p.branch_name}`} value={data.build_code} testId="portal-build-code" />
                : <div data-testid="portal-build-pending" className="border border-nc-orange/30 bg-nc-orange/5 p-4 font-mono text-[11px] text-nc-orange">The test build isn't live yet — you'll get an email with the branch code the moment it is. Install the game now.</div>}
              <Code label="Your Tester ID — paste in-game: Settings → Testing" value={t.tester_token} testId="portal-tester-id" />
            </div>
          )}

          <div className="mt-10">
            <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase flex items-center gap-2"><Server className="w-4 h-4" /> Getting in</p>
            <ol className="mt-4 space-y-3">{p.steps.map((s, i) => <li key={s.title} className="flex gap-3"><span className="font-sans font-extrabold text-lg text-nc-orange/70 w-6 shrink-0">{i + 1}</span><div><p className="font-sans font-bold uppercase tracking-widest text-xs text-zinc-100">{s.title}</p><p className="font-mono text-[11px] text-zinc-500 leading-relaxed mt-0.5">{s.text}</p></div></li>)}</ol>
            <p className="mt-4 font-mono text-[10px] text-zinc-500 border-l-2 border-nc-orange/40 pl-3">{p.nda_note}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-white/10 bg-black/40 p-5">
            <div className="flex items-center justify-between gap-3">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400 flex items-center gap-2">{telemetryOn ? <ShieldCheck className="w-4 h-4 text-nc-green" /> : <ShieldOff className="w-4 h-4 text-nc-orange" />} Performance telemetry</p>
              <button data-testid="portal-telemetry-toggle" onClick={toggleTelemetry} className={`font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors ${telemetryOn ? "border-white/20 text-zinc-300 hover:border-white/40" : "border-nc-green text-nc-green hover:bg-nc-green/10"}`}>{telemetryOn ? "Pause" : "Resume"}</button>
            </div>
            <p className="font-mono text-[11px] text-zinc-500 leading-relaxed mt-3">{telemetryOn ? "On — FPS, frame times, ping, VRAM/RAM, hitches, zone and player count while you play. Never files or keystrokes." : "Paused — we record nothing until you resume."}</p>
            <div className="grid grid-cols-3 gap-3 mt-4">
              {[["Sessions", t.stats?.sessions || 0], ["Avg FPS", t.stats?.avg_fps || "—"], ["Avg ping", t.stats?.avg_ping ? `${t.stats.avg_ping} ms` : "—"]].map(([l, v]) => <div key={l}><p className="font-sans font-extrabold text-xl text-white">{v}</p><p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500">{l}</p></div>)}
            </div>
          </div>

          <div className="border border-white/10 bg-black/40 p-5">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400 flex items-center gap-2"><Activity className="w-4 h-4 text-nc-blue" /> Your sessions</p>
            {sessions.length === 0 ? <p data-testid="portal-no-sessions" className="font-mono text-[11px] text-zinc-500 mt-3">No telemetry yet — it starts the first time you join {p.server_name} with your Tester ID linked.</p> : (
              <ul className="mt-3 divide-y divide-white/10">{sessions.map((s) => (
                <li key={s.id} data-testid="portal-session" className="py-3">
                  <div className="flex items-center justify-between gap-2"><p className="font-mono text-[11px] text-zinc-200">{new Date(s.started_at).toLocaleString()}</p><span className="font-mono text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 border border-white/15 text-zinc-300">{s.bottleneck?.label}</span></div>
                  <p className="font-mono text-[10px] text-zinc-500 mt-1">{s.agg?.fps_avg ? `${s.agg.fps_avg} fps avg · 1% low ${s.agg.fps_1low_avg} · ping ${s.agg.ping_avg} ms · ${s.samples_count} samples` : `${s.samples_count} samples`}</p>
                </li>))}</ul>
            )}
          </div>

          <div className="border border-white/10 bg-black/40 p-5">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400 flex items-center gap-2"><KeyRound className="w-4 h-4 text-nc-orange" /> Report bugs</p>
            {p.reporting.map((r) => <p key={r.situation} className="font-mono text-[11px] text-zinc-500 leading-relaxed mt-2"><span className="text-zinc-300">{r.situation}:</span> {r.action}</p>)}
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
